/* ============================================================
   bd-section-collection-tabs.js
   assets/bd-section-collection-tabs.js

   1. Tab switching (show/hide panels)
   2. Mobile carousel per panel (swipe + arrows + dots)

   Wishlist / Quick View / Add to Cart are now handled globally
   by assets/bd-wishlist.js and assets/bd-quick-view.js (event
   delegation on document), so this file no longer owns them.
   ============================================================ */

(function () {
  'use strict';

  var MOBILE_BP = 600;

  /* ── Per-panel carousel state ─────────────────────────────── */
  var carousels = new WeakMap();

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  /* ── Init tabs ─────────────────────────────────────────────── */
  function initTabs(section) {
    var tabBtns = Array.from(section.querySelectorAll('[data-ct-tabs] .ct-tab'));
    var panels  = Array.from(section.querySelectorAll('.ct-panel'));

    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.dataset.tabIndex);

        tabBtns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');

        panels.forEach(function (p) { p.classList.remove('is-active'); });
        var targetPanel = panels[idx];
        if (targetPanel) {
          targetPanel.classList.add('is-active');
          /* init carousel for this panel if not already done */
          if (!carousels.has(targetPanel)) {
            initCarousel(targetPanel);
          }
        }
      });
    });

    /* init first panel immediately */
    if (panels[0]) initCarousel(panels[0]);
  }

  /* ── Init carousel for a panel ─────────────────────────────── */
  function initCarousel(panel) {
    var track     = panel.querySelector('[data-ct-track]');
    var trackWrap = panel.querySelector('[data-ct-track-wrap]');
    var dotsWrap  = panel.querySelector('[data-ct-dots]');
    var prevBtn   = panel.querySelector('[data-ct-prev]');
    var nextBtn   = panel.querySelector('[data-ct-next]');

    if (!track || !trackWrap) return;

    var cards      = Array.from(track.querySelectorAll('[data-ct-card]'));
    var totalCards = cards.length;
    var currentIdx = 0;

    carousels.set(panel, true);

    if (totalCards === 0) return;

    /* ── Helpers ─────────────────────────────────────────────── */
    function getCardWidth() {
      var card = track.querySelector('[data-ct-card]');
      if (!card) return 0;
      var gap = parseInt(window.getComputedStyle(track).gap) || 16;
      return card.offsetWidth + gap;
    }

    function goTo(idx, animate) {
      if (!isMobile()) return;
      if (idx < 0) idx = 0;
      if (idx >= totalCards) idx = totalCards - 1;
      currentIdx = idx;
      track.style.transition = animate !== false
        ? 'transform 0.42s cubic-bezier(0.25,0.46,0.45,0.94)'
        : 'none';
      track.style.transform = 'translateX(-' + (currentIdx * getCardWidth()) + 'px)';
      updateDots();
      if (prevBtn) prevBtn.disabled = currentIdx <= 0;
      if (nextBtn) nextBtn.disabled = currentIdx >= totalCards - 1;
    }

    /* ── Dots ─────────────────────────────────────────────────── */
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (var i = 0; i < totalCards; i++) {
        (function (di) {
          var dot = document.createElement('button');
          dot.className = 'ct-dot' + (di === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to product ' + (di + 1));
          dot.addEventListener('click', function () { goTo(di, true); });
          dotsWrap.appendChild(dot);
        })(i);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      dotsWrap.querySelectorAll('.ct-dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === currentIdx);
      });
    }

    buildDots();

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIdx - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIdx + 1, true); });

    /* ── Touch + mouse swipe ─────────────────────────────────── */
    var dragStartX    = null;
    var dragStartY    = null;
    var isDragging    = false;
    var dragBaseOffset = 0;

    function getOffset() {
      var matrix = window.getComputedStyle(track).transform;
      if (matrix && matrix !== 'none') {
        var m = matrix.match(/matrix.*\((.+)\)/);
        if (m) return parseFloat(m[1].split(', ')[4]) || 0;
      }
      return -(currentIdx * getCardWidth());
    }

    function onStart(x, y) {
      if (!isMobile()) return;
      dragStartX     = x;
      dragStartY     = y;
      isDragging     = false;
      dragBaseOffset = getOffset();
      track.style.transition = 'none';
      track.style.transform  = 'translateX(' + dragBaseOffset + 'px)';
    }

    function onMove(x, y) {
      if (dragStartX === null || !isMobile()) return;
      var dx = x - dragStartX;
      var dy = y - dragStartY;
      if (!isDragging) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragStartX = null; dragStartY = null;
          return;
        }
        isDragging = true;
      }
      track.style.transform = 'translateX(' + (dragBaseOffset + dx) + 'px)';
    }

    function onEnd(x) {
      if (dragStartX === null || !isMobile()) return;
      var diff = dragStartX - x;
      if (isDragging && Math.abs(diff) > 40) {
        goTo(currentIdx + (diff > 0 ? 1 : -1), true);
      } else {
        goTo(currentIdx, true);
      }
      dragStartX = null; dragStartY = null; isDragging = false;
    }

    trackWrap.addEventListener('mousedown', function (e) {
      if (!isMobile()) return;
      e.preventDefault();
      onStart(e.clientX, e.clientY);
    });
    window.addEventListener('mousemove', function (e) {
      if (dragStartX !== null) onMove(e.clientX, e.clientY);
    });
    window.addEventListener('mouseup', function (e) {
      if (dragStartX !== null) onEnd(e.clientX);
    });
    trackWrap.addEventListener('touchstart', function (e) {
      onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    trackWrap.addEventListener('touchmove', function (e) {
      if (dragStartX === null) return;
      if (isDragging) e.preventDefault();
      onMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: false });
    trackWrap.addEventListener('touchend', function (e) {
      if (dragStartX !== null) onEnd(e.changedTouches[0].clientX);
    });

    /* reset on resize */
    window.addEventListener('resize', function () {
      if (!isMobile()) {
        track.style.transform  = '';
        track.style.transition = '';
        currentIdx = 0;
        updateDots();
      }
    });
  }

  /* ── Boot ──────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll('[data-ct-section]').forEach(function (section) {
      initTabs(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-ct-section]');
    if (!s) return;
    initTabs(s);
  });

})();